import "./Reader.css";
import { Loader } from "@mantine/core";
import { useNavigate, useParams } from "react-router";
import { getConfig } from "../../lib/config.ts";
import { useIframeProgressSync } from "../../lib/hooks/use-iframe-progress-sync.ts";
import { useIframeUserSettingsSync } from "../../lib/hooks/use-iframe-user-settings-sync.ts";
import {
	useDocumentProgress,
	useEmbedCode,
	useUserSettings,
} from "../../lib/queries.ts";

/**
 * Fetches the embed code and document progress, renders the reader iframe, and listens for page navigation events to update progress.
 * @returns {JSX.Element}
 */
export function Reader() {
	const { code } = useParams();
	const { data: embedData, isLoading: isEmbedCodeLoading } = useEmbedCode(
		code ?? "",
	);
	const { data: progressData, isLoading: isProgressLoading } =
		useDocumentProgress(embedData?.readiantId);
	const { data: userSettingsData, isLoading: isUserSettingsLoading } =
		useUserSettings();
	const navigate = useNavigate();
	const readiantId = embedData?.readiantId;

	// React to reader events and sync progress/user settings with the backend.
	useIframeProgressSync(readiantId);
	useIframeUserSettingsSync();

	if (!embedData || isEmbedCodeLoading) return <Loader />;

	const { embedCode } = embedData;

	if (!embedCode) {
		navigate("/ongeldige-code");
		return null;
	}

	if (
		isProgressLoading ||
		!progressData ||
		isUserSettingsLoading ||
		userSettingsData === undefined
	)
		return <Loader />;

	const { page } = progressData;

	const readiantIframeUrl = getConfig("readiantIframeUrl");
	const readiantIframeAttributes = {
		locale: "nl-NL",
		...(page === null ? {} : { page: page.toString() }),
		...removeInvalidValues(userSettingsData ?? {}),
	};
	const params = new URLSearchParams(readiantIframeAttributes).toString();

	return (
		<div className="reader-iframe-container">
			<iframe
				id="reader"
				title="book"
				src={`${readiantIframeUrl}/${embedCode}?${params}`}
				className="reader-iframe"
				sandbox="allow-modals allow-same-origin allow-scripts"
				width="100%"
				height="100%"
			/>
		</div>
	);
}

function removeInvalidValues(obj: object): Record<string, string> {
	const cleanedObj: Record<string, string> = {};
	for (const [key, value] of Object.entries(obj)) {
		if (
			(typeof value === "string" && value !== "") ||
			typeof value === "number"
		) {
			cleanedObj[key] = String(value);
		}
	}
	return cleanedObj;
}
